import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Clock, CreditCard, MessageCircle, Star, Truck, CheckCircle2, AlertTriangle, XCircle, Navigation, Shield, Gavel, Trophy } from "lucide-react";
import { api, STATUS_LABELS, CATEGORIES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

export default function TaskDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [addrFull, setAddrFull] = useState("");
  const [addrOpen, setAddrOpen] = useState(false);
  const [bids, setBids] = useState([]);
  const [bidOpen, setBidOpen] = useState(false);
  const [bidAmount, setBidAmount] = useState("");
  const [bidMessage, setBidMessage] = useState("");

  const helperApproved = (user?.helper_profile || {}).approved === true;
  const helperLockReason = user?.role === "helper" && !helperApproved
    ? "Ton profil helper doit être approuvé par Go Helper avant d'agir sur les tâches."
    : null;

  const load = async () => {
    try {
      const { data } = await api.get(`/tasks/${id}`);
      setTask(data);
      if (data.address_full) setAddrFull(data.address_full);
      if (data.pricing_mode === "bids") {
        const r = await api.get(`/tasks/${id}/bids`);
        setBids(r.data);
      }
    } catch {
      toast.error("Tâche introuvable");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  if (loading) return <div className="text-center py-10 text-[#475569]">Chargement...</div>;
  if (!task) return <div className="text-center py-10">Introuvable.</div>;

  const s = STATUS_LABELS[task.status];
  const isClient = user.id === task.client_id;
  const isHelper = user.id === task.helper_id;

  const action = async (path, ok = "Mis à jour") => {
    setBusy(true);
    try { await api.post(`/tasks/${id}${path}`); toast.success(ok); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const accept = () => action("/accept", "Tâche acceptée !");
  const onTheWay = () => action("/on-the-way", "Client notifié");
  const complete = () => action("/complete", "Tâche marquée complétée");
  const confirm = () => action("/confirm", "Paiement libéré au helper");
  const cancel = () => action("/cancel", "Tâche annulée");
  const report = () => action("/report", "Problème signalé");

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/checkout/${id}`, { origin_url: window.location.origin });
      if (data.credits_used && data.credits_used > 0) {
        toast.success(`💸 ${data.credits_used.toFixed(2)} $ de crédit appliqué !`);
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur paiement");
      setBusy(false);
    }
  };

  const submitReview = async () => {
    try {
      await api.post(`/reviews/${id}`, { rating, comment });
      toast.success("Merci pour ton évaluation !");
      setReviewOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const submitBid = async () => {
    const amt = Number(bidAmount);
    if (!amt || amt < 5) { toast.error("Montant minimum 5 $"); return; }
    try {
      await api.post(`/tasks/${id}/bids`, { amount: amt, message: bidMessage });
      toast.success("Soumission envoyée !");
      setBidOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const acceptBid = async (bidId) => {
    setBusy(true);
    try {
      await api.post(`/bids/${bidId}/accept`);
      toast.success("Soumission acceptée");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const saveAddress = async () => {
    try {
      await api.post(`/tasks/${id}/confirm-address`, { address_full: addrFull });
      toast.success("Adresse enregistrée");
      setAddrOpen(false);
      await load();
    } catch { toast.error("Erreur"); }
  };

  const catLabel = CATEGORIES.find((c) => c.id === task.category)?.label || task.category;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Link to={-1} className="text-navy font-bold text-sm hover:underline" data-testid="back-link">← Retour</Link>

      <div className="card-surface p-6 md:p-8">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`chip ${s.color}`} data-testid="task-status">{s.fr}</span>
          <span className="chip">{catLabel}</span>
          {task.urgent && <span className="chip-lime">Urgent</span>}
        </div>
        <h1 className="mt-4 text-3xl md:text-4xl font-heading font-black text-navy">{task.title}</h1>
        <div className="flex items-center gap-1 mt-2 text-[#475569]"><MapPin size={16} /> {task.area} · {task.address_full || task.address_approx}</div>
        <p className="mt-4 text-[#475569] whitespace-pre-wrap leading-relaxed">{task.description}</p>
        {task.photos && task.photos.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-2" data-testid="task-photos-display">
            {task.photos.map((p) => (
              <a key={p} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}?auth=${localStorage.getItem("gh_token")}`} target="_blank" rel="noreferrer" className="aspect-square rounded-xl overflow-hidden bg-surface border border-[#E2E8F0] hover:ring-2 hover:ring-lime transition-all">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}?auth=${localStorage.getItem("gh_token")}`} alt="Photo tâche" className="w-full h-full object-cover" loading="lazy" />
              </a>
            ))}
          </div>
        )}
        {task.category === "navette_aeroport" && task.airport_code && (
          <div className="mt-4 p-3 rounded-xl bg-navy/5 border border-navy/10 text-sm" data-testid="airport-info">
            <div className="font-bold text-navy">✈️ Détails du vol</div>
            <div className="text-[#475569] mt-1">
              Aéroport : <span className="font-bold text-navy">{task.airport_code}</span>
              {task.flight_type && <> · {task.flight_type === "departure" ? "Départ" : "Arrivée"}</>}
              {task.flight_datetime && <> · {new Date(task.flight_datetime).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" })}</>}
            </div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-surface p-6">
          <h3 className="text-xs font-black uppercase tracking-wider text-[#94A3B8]">Détail du prix</h3>
          <div className="mt-4 space-y-1 font-mono text-sm">
            <Row l="Prix de base" v={`${task.base_price.toFixed(2)} $`} />
            {task.urgent && <Row l="Urgence (+30 %)" v={`${task.urgency_fee.toFixed(2)} $`} />}
            {task.travel_fee > 0 && <Row l="Frais déplacement" v={`${task.travel_fee.toFixed(2)} $`} />}
            <Row l="Sous-total helper" v={`${task.helper_subtotal.toFixed(2)} $`} bold />
            <Row l="Frais Go Helper" v={`${task.go_helper_fee.toFixed(2)} $`} />
            <div className="border-t border-[#E2E8F0] mt-2 pt-2 flex justify-between text-navy font-bold">
              <span>{isClient ? "Total payé" : "Montant client"}</span>
              <span className="font-heading text-xl">{task.total.toFixed(2)} $</span>
            </div>
            {isHelper && (
              <div className="flex justify-between text-lime font-bold pt-1">
                <span>Tu recevras</span><span className="font-heading text-xl">{task.helper_subtotal.toFixed(2)} $</span>
              </div>
            )}
          </div>
        </div>

        <div className="card-surface p-6">
          <h3 className="text-xs font-black uppercase tracking-wider text-[#94A3B8]">Actions</h3>
          <div className="mt-4 space-y-2">
            {task.helper_id && (
              <Link to={`/messages/${task.id}`} className="btn-outline w-full" data-testid="btn-messages">
                <MessageCircle size={18} /> Messagerie
              </Link>
            )}

            {isHelper && task.client_lat != null && task.client_lng != null && ["paid", "on_the_way", "completed"].includes(task.status) && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${task.client_lat},${task.client_lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline w-full"
                data-testid="btn-directions"
              >
                <Navigation size={18} /> Itinéraire Google Maps
              </a>
            )}

            {/* Helper actions */}
            {user.role === "helper" && !task.helper_id && task.status === "published" && task.pricing_mode !== "bids" && (
              <>
                <button disabled={busy || !helperApproved} onClick={accept} className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed" data-testid="btn-accept">
                  <CheckCircle2 size={18} /> Accepter la tâche
                </button>
                {helperLockReason && (
                  <Link to="/helper/apply" className="block mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 text-center hover:bg-amber-100" data-testid="helper-lock-msg">
                    🔒 {helperLockReason} → Voir ma candidature
                  </Link>
                )}
              </>
            )}
            {user.role === "helper" && !task.helper_id && task.status === "published" && task.pricing_mode === "bids" && (
              <>
                <button disabled={busy || !helperApproved} onClick={() => setBidOpen(true)} className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed" data-testid="btn-open-bid">
                  <Gavel size={18} /> Proposer mon prix
                </button>
                {helperLockReason && (
                  <Link to="/helper/apply" className="block mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 text-center hover:bg-amber-100" data-testid="helper-lock-msg-bid">
                    🔒 {helperLockReason} → Voir ma candidature
                  </Link>
                )}
              </>
            )}
            {isHelper && task.status === "paid" && (
              <button disabled={busy} onClick={onTheWay} className="btn-primary w-full" data-testid="btn-ontheway">
                <Truck size={18} /> Je suis en route
              </button>
            )}
            {isHelper && ["paid", "on_the_way"].includes(task.status) && (
              <button disabled={busy} onClick={complete} className="btn-secondary w-full" data-testid="btn-complete">
                <CheckCircle2 size={18} /> Marquer comme terminé
              </button>
            )}

            {/* Client actions */}
            {isClient && task.status === "accepted" && (
              <>
                <button disabled={busy} onClick={pay} className="btn-primary w-full" data-testid="btn-pay">
                  <CreditCard size={18} /> Payer maintenant · {task.total.toFixed(2)} $
                </button>
                <button onClick={() => setAddrOpen(true)} className="btn-outline w-full" data-testid="btn-address">
                  Confirmer l'adresse complète
                </button>
              </>
            )}
            {isClient && task.status === "completed" && (
              <button disabled={busy} onClick={confirm} className="btn-primary w-full" data-testid="btn-confirm">
                <CheckCircle2 size={18} /> Confirmer et payer le helper
              </button>
            )}
            {isClient && task.status === "paid_out" && (
              <button onClick={() => setReviewOpen(true)} className="btn-primary w-full" data-testid="btn-review">
                <Star size={18} /> Laisser une évaluation
              </button>
            )}

            {/* Cancel / report */}
            {isClient && ["published", "accepted"].includes(task.status) && (
              <button disabled={busy} onClick={cancel} className="btn-outline w-full text-red-600 border-red-300" data-testid="btn-cancel">
                <XCircle size={18} /> Annuler
              </button>
            )}
            {(isClient || isHelper) && !["paid_out", "cancelled"].includes(task.status) && (
              <button disabled={busy} onClick={report} className="text-sm text-[#475569] hover:text-red-600 flex items-center gap-1 mt-2" data-testid="btn-report">
                <AlertTriangle size={14} /> Signaler un problème
              </button>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-[#E2E8F0] text-sm text-[#475569]">
            <div className="flex items-center gap-1"><Clock size={14} /> Créée le {new Date(task.created_at).toLocaleDateString()}</div>
            {task.helper_name && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span>Helper:</span>
                <strong className="text-navy">{task.helper_name}</strong>
                <span className="chip-lime"><Shield size={12} /> Vérifié</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bids section (client view) */}
      {task.pricing_mode === "bids" && isClient && !task.helper_id && (
        <div className="card-surface p-6" data-testid="bids-section">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-black text-xl text-navy flex items-center gap-2">
              <Gavel size={20} /> Soumissions reçues
            </h3>
            <span className="chip">{bids.length}</span>
          </div>
          {task.budget_hint && (
            <p className="text-sm text-[#475569] mt-1">Budget indicatif : <strong>{task.budget_hint.toFixed(2)} $</strong></p>
          )}
          {bids.length === 0 ? (
            <p className="text-[#475569] mt-4">Aucune soumission pour le moment. Les helpers de ta région sont notifiés.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {bids.map((b) => (
                <div key={b.id} className="border-2 border-[#E2E8F0] rounded-xl p-4 hover:border-navy transition-colors" data-testid={`bid-${b.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-navy text-lime font-black flex items-center justify-center">{b.helper_name[0]}</div>
                    <div className="flex-1">
                      <div className="font-bold text-navy flex items-center gap-2 flex-wrap">
                        {b.helper_name}
                        <span className="chip-navy"><Trophy size={10} /> {b.helper_badge}</span>
                      </div>
                      <div className="text-xs text-[#475569] flex items-center gap-2">
                        <Star size={12} className="fill-lime text-lime" /> {(b.helper_rating || 0).toFixed(1)} · {b.helper_tasks_completed} tâches
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading font-black text-2xl text-navy">{b.amount.toFixed(2)} $</div>
                    </div>
                  </div>
                  {b.message && <p className="text-sm text-[#475569] mt-2 italic">"{b.message}"</p>}
                  <button onClick={() => acceptBid(b.id)} disabled={busy} className="btn-primary w-full mt-3 py-2.5 text-sm" data-testid={`accept-bid-${b.id}`}>
                    <CheckCircle2 size={16} /> Accepter cette soumission
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={bidOpen} onOpenChange={setBidOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Propose ton prix</DialogTitle></DialogHeader>
          {task?.budget_hint && <p className="text-sm text-[#475569]">Budget indicatif du client : <strong>{task.budget_hint.toFixed(2)} $</strong></p>}
          <div>
            <label className="text-sm font-bold text-navy">Ton montant ($)</label>
            <input type="number" min="5" className="input-base mt-1" value={bidAmount} onChange={(e) => setBidAmount(e.target.value)} placeholder="35" data-testid="bid-amount" />
          </div>
          <div>
            <label className="text-sm font-bold text-navy">Message (optionnel)</label>
            <textarea rows={3} className="input-base mt-1" value={bidMessage} onChange={(e) => setBidMessage(e.target.value)} placeholder="Pourquoi c'est toi la meilleure option..." data-testid="bid-message" />
          </div>
          <DialogFooter>
            <button onClick={submitBid} className="btn-primary w-full" data-testid="submit-bid">Envoyer ma soumission</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Évalue ton helper</DialogTitle></DialogHeader>
          <div className="flex justify-center gap-2 py-4">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
                <Star size={36} className={n <= rating ? "fill-lime text-lime" : "text-[#CBD5E1]"} />
              </button>
            ))}
          </div>
          <textarea className="input-base" rows={3} placeholder="Un mot pour le helper..." value={comment} onChange={(e) => setComment(e.target.value)} data-testid="input-comment" />
          <DialogFooter>
            <button onClick={submitReview} className="btn-primary w-full" data-testid="submit-review">Envoyer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addrOpen} onOpenChange={setAddrOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adresse complète</DialogTitle></DialogHeader>
          <p className="text-sm text-[#475569]">Visible seulement par ton helper après paiement.</p>
          <input className="input-base mt-3" value={addrFull} onChange={(e) => setAddrFull(e.target.value)} placeholder="123 rue Exemple, Montréal" data-testid="input-fulladdress" />
          <DialogFooter>
            <button onClick={saveAddress} className="btn-primary w-full" data-testid="save-address">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ l, v, bold }) {
  return <div className={`flex justify-between ${bold ? "text-navy font-bold" : "text-[#475569]"}`}><span>{l}</span><span>{v}</span></div>;
}
